import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  SvgIcon,
  Paper,
  useTheme,
  Chip,
} from '@mui/material';
import {
  Verified,
  AccessTime,
  Savings,
  WorkspacePremium,
  RocketLaunch,
  School,
  Launch,
  MailOutline,
} from '@mui/icons-material';
import { headerGradient } from '../theme';

// Título com sublinhado (igual “Revista”)
const SectionTitle: React.FC<{ children: React.ReactNode; width?: number }> = ({ children, width = 160 }) => (
  <Typography
    variant="h5"
    sx={{
      fontWeight: 700,
      color: 'primary.main',
      mb: 2,
      position: 'relative',
      pb: 1,
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: 0,
        width,
        height: 3,
        bgcolor: 'secondary.main',
        borderRadius: 2,
      },
    }}
  >
    {children}
  </Typography>
);

type Motivo = { icon: React.ElementType; text: string };
const motivos: Motivo[] = [
  { icon: Verified, text: 'Curso Superior de Tecnologia em Podologia com nota 4 no MEC.' },
  { icon: AccessTime, text: 'Redução de até 320 horas no curso superior.' },
  { icon: WorkspacePremium, text: 'Menos tempo de aulas semanais com formação otimizada.' },
  { icon: Savings, text: 'Economia de tempo e de recursos financeiros.' },
  { icon: School, text: 'Aproveitamento de Unidades Curriculares do curso técnico em todos os períodos.' },
];

const ProjetoVerticaliza: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ pt: { xs: 6, md: 10 } }}>
      {/* HERO / título central com chips */}
      <Box sx={{ textAlign: 'center', px: { xs: 2, md: 6 } }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            display: 'inline-block',
            position: 'relative',
            pb: 1.5,
            letterSpacing: 0.2,
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 0,
              width: 320,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: theme.shape.borderRadius,
            },
          }}
        >
          Projeto Verticaliza SENAC Amazonas – Podologia
        </Typography>
        <Typography sx={{ mt: 1, fontWeight: 600 }}>
          Do Curso Técnico ao Curso Superior de Tecnologia
        </Typography>
        <Typography sx={{ mt: 1.5, maxWidth: 820, mx: 'auto', lineHeight: 1.8 }}>
          Iniciativa que fortalece o percurso formativo contínuo, articulando competências, saberes técnicos e acadêmicos,
          promovendo ascensão educacional e profissional.
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Chip icon={<School />} label="Do curso técnico ao superior" color="primary" variant="outlined" />
          <Chip icon={<RocketLaunch />} label="Aproveitamento de estudos" color="secondary" variant="outlined" />
          <Chip icon={<WorkspacePremium />} label="Formação ágil e integrada" color="success" variant="outlined" />
        </Stack>
      </Box>

      {/* Intro + mídia lateral (layout no estilo Revista) */}
      <Box sx={{ px: { xs: 2, md: 6 }, mt: 4 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <SectionTitle width={360}>O que é o Projeto Verticaliza SENAC Amazonas?</SectionTitle>
            <Typography paragraph sx={{ textAlign: 'justify', lineHeight: 1.9 }}>
              O Projeto Verticaliza SENAC Amazonas – Podologia é uma proposta pedagógica integrada que possibilita aos
              egressos do Curso Técnico em Podologia do SENAC AM o ingresso no Curso Superior de Tecnologia em Podologia,
              com aproveitamento de estudos, redução de carga horária e otimização do tempo de formação.
            </Typography>
            <Typography paragraph sx={{ textAlign: 'justify', lineHeight: 1.9 }}>
              Podem participar egressos do Curso Técnico de Podologia, realizado no Senac, no período de 2017 a 2025.
            </Typography>

            <SectionTitle width={160}>Como funciona?</SectionTitle>
            <Typography paragraph sx={{ textAlign: 'justify', lineHeight: 1.9 }}>
              O aluno egresso do Curso Técnico em Podologia do SENAC AM tem o aproveitamento completo de 7 Unidades
              Curriculares no CST em Podologia, distribuídas ao longo dos períodos do curso:
            </Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 1.9, mt: 0 }}>
              <li>I Período: 1 disciplina</li>
              <li>II Período: 2 disciplinas</li>
              <li>III Período: 1 disciplina</li>
              <li>IV Período: 3 disciplinas</li>
            </Box>
            <Typography sx={{ textAlign: 'justify', lineHeight: 1.9 }}>
              Esse aproveitamento garante uma formação mais ágil, sem prejuízo da qualidade acadêmica.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Faixa com destaque + banner promocional (slot) */}
      <Box
        sx={{
          mt: 6,
          py: 4,
          color: '#fff',
          background: headerGradient,
          width: '100vw',
          maxWidth: '100vw',
          ml: 'calc(50% - 50vw)',
          mr: 'calc(50% - 50vw)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Quer acelerar sua graduação em Podologia?
              </Typography>
              <Typography sx={{ opacity: 0.95 }}>
                Com o Verticaliza, você transforma as conquistas do técnico em vantagem no ensino superior, reduzindo
                tempo e carga horária total de formação.
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Launch />}
                  href="/graduacao"
                >
                  Conheça o CST em Podologia
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<MailOutline />}
                  href="mailto:faculdade@am.senac.br"
                >
                  Fale com a Central
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* SLOT #2 — banner largo promocional */}
              <Box
                component="img"
                src="./assets/banners/VERTICALIZA_PODOLOGIA_banner_site.jpg" // 👈 SEU CAMINHO AQUI
                alt="Aluno estudando"
                loading="lazy"
                sx={{
                  width: '900px',
                  height: '400px',
                  display: 'block',
                  borderRadius: 2,
                  aspectRatio: '4 / 5',
                  objectFit: 'cover',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 5 Motivos (cards como na Revista) */}
      <Box sx={{ px: { xs: 2, md: 6 }, mt: 6 }}>
        <SectionTitle>5 Motivos para escolher o Verticaliza</SectionTitle>
        <Grid container spacing={3}>
          {motivos.map((m, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <SvgIcon component={m.icon} sx={{ fontSize: 28, color: 'primary.main', mt: 0.5 }} />
                  <Typography sx={{ lineHeight: 1.7 }}>{m.text}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>


      {/* Observações */}
      <Box sx={{ px: { xs: 2, md: 6 }, mt: 6 }}>
        <SectionTitle>Observações</SectionTitle>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, lineHeight: 1.8 }}>
          <Typography sx={{ mb: 1 }}>Para participar do Verticaliza, o aluno deverá:</Typography>
          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            <li>Realizar o processo seletivo do Curso Superior de Tecnologia em Podologia.</li>
            <li>Comparecer na Faculdade Senac, na rua 10 de julho, 11 – Centro.</li>
            <li>Solicitar o aproveitamento de estudos.</li>
            <li>Efetuar a matrícula regular no curso.</li>
          </Box>
        </Paper>
      </Box>

      {/* CTA final no estilo Revista */}
      <Box
        sx={{
          mt: 6,
          py: 4,
          color: '#fff',
          background: headerGradient,
          width: '100vw',
          maxWidth: '100vw',
          ml: 'calc(50% - 50vw)',
          mr: 'calc(50% - 50vw)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 6 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            A gente se encontra amanhã: fale conosco
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button variant="contained" color="secondary" startIcon={<MailOutline />} href="mailto:faculdade@am.senac.br">
              faculdade@am.senac.br
            </Button>
            <Button variant="outlined" color="inherit" startIcon={<Launch />} href="tel:0800928888">
              0800 92 8888
            </Button>
            <Button variant="outlined" color="inherit" startIcon={<Launch />} href="tel:9231999958">
              (92) 3199-9958
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Rodapé curto */}
      <Box sx={{ px: { xs: 2, md: 6 }, mt: 4, pb: 8 }}>
        <Typography>
          <strong>Contato:</strong> faculdade@am.senac.br | 0800 92 8888 | (92) 3199-9958
        </Typography>
      </Box>
    </Box>
  );
};

export default ProjetoVerticaliza;
